import React, { useState } from "react";
import { Star, ChevronRight, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import type { TopPost } from "@/hooks/useTopPerformingPosts";
import { formatCompactNumber, formatPercentage } from "@/lib/utils/formatNumber";

const PLATFORM_LOGO: Record<string, string> = {
  instagram: "/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png",
};

function renderPublishedDate(isoString: string | null): React.ReactNode {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "-";

  const monthDay = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);

  const year = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
  }).format(date);

  return (
    <div className="flex flex-col whitespace-nowrap text-sm leading-tight">
      <span>{monthDay},</span>
      <span>{year}</span>
    </div>
  );
}

export interface TopPostsTableProps {
  data: TopPost[];
  title?: string;
  subtitle?: string;
  info?: string;
  showViewAll?: boolean;
  onViewAll?: () => void;
  isLoading?: boolean;
}

const SKELETON_ROWS = 5;
const COLUMN_COUNT = 10;

interface HoveredThumbnailInfo {
  thumbnailUrl: string;
  contentType: string;
  influencerName: string;
  x: number;
  y: number;
}

interface PostThumbnailCellProps {
  thumbnailUrl: string | null;
  contentType: string;
  influencerName: string;
  postUrl: string | null;
  platform?: string;
  onHover: (info: HoveredThumbnailInfo | null) => void;
}

const PostThumbnailCell: React.FC<PostThumbnailCellProps> = ({
  thumbnailUrl,
  contentType,
  influencerName,
  postUrl,
  platform,
  onHover,
}) => {
  const [hasError, setHasError] = useState(false);
  const logoSrc =
    (platform && PLATFORM_LOGO[platform.toLowerCase()]) ||
    PLATFORM_LOGO.instagram;

  return (
    <div className="flex items-center space-x-3.5 py-0.5">
      {thumbnailUrl && !hasError ? (
        <div
          className="relative shrink-0 cursor-pointer"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            onHover({
              thumbnailUrl,
              contentType,
              influencerName,
              x: rect.right + 12,
              y: rect.top + rect.height / 2,
            });
          }}
          onMouseLeave={() => onHover(null)}
        >
          {postUrl ? (
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-12 h-16 rounded-lg overflow-hidden border border-gray-200/90 bg-gray-900 shrink-0 block ring-1 ring-black/5 shadow-xs transition-all duration-200 hover:shadow-md hover:border-purple-400"
              title={`View ${influencerName}'s ${contentType}`}
            >
              <img
                src={thumbnailUrl}
                alt={`${influencerName}'s ${contentType}`}
                className="w-full h-full object-cover object-center transition-transform duration-200 group-hover:scale-105"
                onError={() => {
                  setHasError(true);
                  onHover(null);
                }}
              />
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-white/95 shadow-xs flex items-center justify-center">
                <img
                  src={logoSrc}
                  alt={platform || "Instagram"}
                  className="w-3 h-3 object-contain"
                />
              </div>
            </a>
          ) : (
            <div className="relative w-12 h-16 rounded-lg overflow-hidden border border-gray-200/90 bg-gray-900 shrink-0 ring-1 ring-black/5 shadow-xs">
              <img
                src={thumbnailUrl}
                alt={`${influencerName}'s ${contentType}`}
                className="w-full h-full object-cover object-center"
                onError={() => {
                  setHasError(true);
                  onHover(null);
                }}
              />
              <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-white/95 shadow-xs flex items-center justify-center">
                <img
                  src={logoSrc}
                  alt={platform || "Instagram"}
                  className="w-3 h-3 object-contain"
                />
              </div>
            </div>
          )}
        </div>
      ) : postUrl ? (
        <a
          href={postUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative w-12 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shrink-0 flex items-center justify-center hover:border-purple-300 transition-all shadow-xs"
          title={`View ${influencerName}'s ${contentType}`}
        >
          <img
            src={logoSrc}
            alt={platform || "Instagram"}
            className="w-5 h-5 object-contain"
          />
        </a>
      ) : (
        <div className="w-12 h-16 rounded-lg bg-gray-100 border border-gray-200 shrink-0 flex items-center justify-center">
          <img
            src={logoSrc}
            alt={platform || "Instagram"}
            className="w-5 h-5 object-contain"
          />
        </div>
      )}

      {postUrl ? (
        <a
          href={postUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 hover:underline transition-colors whitespace-nowrap"
        >
          <span>View {contentType ? (contentType.charAt(0).toUpperCase() + contentType.slice(1).toLowerCase()) : "Post"}</span>
          <ExternalLink className="w-3.5 h-3.5 opacity-80" />
        </a>
      ) : (
        <span className="text-gray-400 text-sm capitalize">{contentType || "-"}</span>
      )}
    </div>
  );
};

export const TopPostsTable: React.FC<TopPostsTableProps> = ({
  data,
  title = "Top Performing Posts",
  subtitle = "Discover the content that's driving the best results across your campaign.",
  info = "Discover the content that's driving the best results across your campaign.",
  showViewAll = true,
  onViewAll,
  isLoading = false,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [hoveredThumbnail, setHoveredThumbnail] = useState<HoveredThumbnailInfo | null>(null);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative">
      {/* Floating Preview Portal */}
      {hoveredThumbnail && (
        <div
          className="fixed z-[9999] pointer-events-none w-36 rounded-xl bg-white shadow-2xl border border-gray-200 p-1.5 animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: `${hoveredThumbnail.x}px`,
            top: `${Math.max(130, Math.min(window.innerHeight - 160, hoveredThumbnail.y))}px`,
            transform: "translateY(-50%)",
          }}
        >
          <div className="relative w-full aspect-[9/16] rounded-lg overflow-hidden bg-gray-900 border border-gray-100 shadow-inner">
            <img
              src={hoveredThumbnail.thumbnailUrl}
              alt={hoveredThumbnail.contentType}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-white/95 shadow-xs flex items-center justify-center">
              <img
                src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                alt="Instagram"
                className="w-3.5 h-3.5 object-contain"
              />
            </div>
          </div>
          <div className="px-1 pt-1.5 pb-0.5 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-800 truncate">{hoveredThumbnail.influencerName}</span>
            <span className="text-[10px] text-purple-600 font-medium capitalize">{hoveredThumbnail.contentType}</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
            <Star className="w-6 h-6 text-purple-600" strokeWidth={1.8} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {info && (
                <div
                  className="relative inline-flex items-center"
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <div
                    className="w-4 h-4 rounded-full border border-gray-400 text-gray-400 hover:text-gray-600 hover:border-gray-600 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                    aria-label="More information"
                  >
                    <span className="text-[10px] font-bold leading-none select-none">i</span>
                  </div>

                  {tooltipOpen && (
                    <div className="absolute left-0 top-6 z-50 w-64 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl leading-relaxed">
                      <div className="absolute -top-1.5 left-2 w-3 h-3 bg-gray-900 rotate-45 rounded-sm" />
                      <span className="relative z-10">{info}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>

        {showViewAll && (
          <Button
            variant="outline"
            type="button"
            onClick={onViewAll}
          >
            <span>View all Posts</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-t-xl">
        <table className="w-full">
          <thead className="bg-pink-50">
            <tr className="border-b border-pink-100 text-gray-900 text-sm">
              <th className="text-left py-3 px-4 font-semibold rounded-tl-xl">Post</th>
              <th className="text-left py-3 px-4 font-semibold">Influencers</th>
              <th className="text-center py-3 px-4 font-semibold">Views</th>
              <th className="text-center py-3 px-4 font-semibold">Likes</th>
              <th className="text-center py-3 px-4 font-semibold">Comments</th>
              <th className="text-center py-3 px-4 font-semibold">Shares</th>
              <th className="text-center py-3 px-4 font-semibold">Post Eng.</th>
              <th className="text-center py-3 px-4 font-semibold">ER% Followers</th>
              <th className="text-center py-3 px-4 font-semibold">ER% Views</th>
              <th className="text-left py-3 px-4 font-semibold rounded-tr-xl">Published</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b border-gray-100">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="w-11 h-14 rounded-lg" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </td>
                  {Array.from({ length: COLUMN_COUNT - 3 }).map((_, j) => (
                    <td key={j} className="py-3 px-4 text-center">
                      <Skeleton className="h-4 w-12 mx-auto" />
                    </td>
                  ))}
                  <td className="py-3 px-4">
                    <Skeleton className="h-4 w-16" />
                  </td>
                </tr>
              ))}

            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={COLUMN_COUNT} className="py-12 text-center text-gray-500">
                  No post data available for this campaign.
                </td>
              </tr>
            )}

            {!isLoading &&
              data.map((post) => {
                const totalEng =
                  post.engagement ??
                  ((post.likes || 0) + (post.comments || 0) + (post.shares || 0));

                const erViews =
                  post.erViews !== null && post.erViews !== undefined
                    ? post.erViews
                    : post.views && post.views > 0 && post.engagementRate
                      ? post.engagementRate
                      : null;

                return (
                  <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <PostThumbnailCell
                        thumbnailUrl={post.thumbnailUrl}
                        contentType={post.contentType}
                        influencerName={post.influencerName}
                        postUrl={post.postUrl}
                        platform={post.platform}
                        onHover={setHoveredThumbnail}
                      />
                    </td>

                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        {post.influencerAvatar ? (
                          <img
                            src={post.influencerAvatar}
                            alt={post.influencerName}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {post.influencerName
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-gray-900">{post.influencerName}</span>
                      </div>
                    </td>

                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {formatCompactNumber(post.views)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {formatCompactNumber(post.likes)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {formatCompactNumber(post.comments)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {formatCompactNumber(post.shares)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {formatCompactNumber(totalEng)}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {post.erFollowers !== null && post.erFollowers !== undefined
                        ? formatPercentage(post.erFollowers)
                        : "-"}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-center font-medium">
                      {erViews !== null && erViews !== undefined
                        ? formatPercentage(erViews)
                        : "-"}
                    </td>
                    <td className="py-3 px-4 text-gray-600">
                      {renderPublishedDate(post.publishedOn)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopPostsTable;
